import axios from 'axios';
import API_URL from '../config';
import logger from '../utils/logger';


// Helper functions for authentication
const getToken = () => localStorage.getItem('token');

const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Create a new invitation
export const createInvitation = async (invitationData) => {
  try {
    const token = getToken();
    setAuthToken(token);
    
    const response = await axios.post(
      `${API_URL}/api/invitations`,
      invitationData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    logger.error('Error creating invitation:', error);
    throw error;
  }
};

// Get all invitations
export const getAllInvitations = async () => {
  try {
    const token = getToken();
    setAuthToken(token);
    
    logger.debug('Fetching invitations with token:', token ? 'Token exists' : 'No token');
    const response = await axios.get(`${API_URL}/api/invitations`);
    logger.debug('Raw invitation response:', response);
    logger.debug('Invitation data received:', response.data);
    
    // Check if we have valid data
    if (!Array.isArray(response.data)) {
      logger.error('API returned non-array data:', response.data);
      return [];
    }
    
    // Log each invitation for debugging
    response.data.forEach((invitation, index) => {
      console.log(`Invitation ${index}:`, {
        id: invitation._id,
        name: invitation.name,
        email: invitation.email,
        code: invitation.code,
        status: invitation.status,
        createdAt: invitation.createdAt,
        expiresAt: invitation.expiresAt
      });
    });
    
    return response.data;
  } catch (error) {
    logger.error('Error fetching invitations:', error);
    throw error;
  }
};

// Verify an invitation code
export const verifyInvitationCode = async (code) => {
  try {
    // No token needed for public verification
    const response = await axios.get(`${API_URL}/api/invitations/verify/${code}`);
    
    // Make sure we have a valid response
    if (response.data && typeof response.data.valid !== 'undefined') {
      return {
        valid: response.data.valid,
        email: response.data.email || '',
        name: response.data.name || '',
        message: response.data.message || ''
      };
    } else {
      // If response structure is unexpected, return invalid
      return {
        valid: false,
        email: '',
        name: '',
        message: 'Invalid response from server'
      };
    }
  } catch (error) {
    logger.error('Error verifying invitation code:', error);
    // Return a structured error response instead of throwing
    return {
      valid: false,
      email: '',
      name: '',
      message: error.response?.data?.message || 'Error verifying invitation code'
    };
  }
};

// Mark an invitation as used
export const markInvitationAsUsed = async (code) => {
  try {
    const response = await axios.put(`${API_URL}/api/invitations/use/${code}`);
    return response.data;
  } catch (error) {
    logger.error('Error marking invitation as used:', error);
    throw error;
  }
};

// Delete an invitation
export const deleteInvitation = async (id) => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    setAuthToken(token);
    
    const response = await axios.delete(`${API_URL}/api/invitations/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    logger.error('Error deleting invitation:', error);
    logger.error('Error stack trace:', error.stack);
    throw error;
  }
};
